import React, { useState, useEffect } from "react";

import classnames from "classnames";
import PropTypes from "prop-types";
import { motion } from "framer-motion";
import { NavLink, useLocation } from "react-router-dom";

import { pathOr } from "ramda";

import { Typography } from "clearsense-ui/components";

import { LeftArrow, RightArrow } from "icons";
import NestedItems from "./NestedItems";
import Item from "./Item";
import NavIconWrapper from "./NavIconWrapper";

const Sidebar = ({
  navLinks,
  footerLinks = [],
  isCollapsed = false,
  collapsible = true,
  onCollapse,
}) => {
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(isCollapsed);

  const toggleCollapse =
    onCollapse || (() => setCollapsed(collapsed => !collapsed));

  const open = {};
  const initializeOpen = (items = navLinks) => {
    items.map(link => {
      if (link?.items) {
        open[link?.label] = true;
        initializeOpen(link.items);
      }
    });
    return open;
  };

  const [isOpen, setIsOpen] = useState(initializeOpen());

  useEffect(() => {
    setCollapsed(isCollapsed);
  }, [isCollapsed]);

  const variants = {
    expanded: { maxWidth: 320 },
    collapsed: { maxWidth: 80 },
  };

  const isFeatureActive = items =>
    items?.some(item => {
      if (item.items) return isFeatureActive(item.items);
      return location.pathname.includes(item.to);
    });

  return (
    <motion.div
      animate={collapsed ? "collapsed" : "expanded"}
      variants={variants}
      transition={{ duration: 0.3 }}
      className={classnames(
        "cs-ui-sidebar flex flex-col flex-shrink-0 overflow-y-auto overflow-x-hidden",
        {
          "cs-ui-sidebar--collapsed": collapsed,
        }
      )}
      data-cy="data-management-sidebar-links"
    >
      <div className="cs-ui-sidebar__links">
        {navLinks.map(
          (
            {
              label: mainLabel,
              to: mainRoute,
              icon,
              items,
              showIcon,
              ...otherProps
            },
            mainIndex
          ) => {
            const isActive = items?.length
              ? items.some(({ to }) => location.pathname.includes(to))
              : location.pathname.includes(mainRoute);
            const shouldOpenSidebar = items && collapsed;
            const to =
              shouldOpenSidebar && isActive
                ? location.pathname
                : pathOr(mainRoute, [0, "to"], items);
            return (
              <React.Fragment key={mainIndex}>
                <NavLink
                  to={to}
                  className={classnames(
                    "flex items-center no-underline select-none cs-ui-sidebar__link",
                    { "bg-transparent": items?.length },
                    {
                      active: isFeatureActive(items),
                    }
                  )}
                  onClick={() => {
                    !collapsed &&
                      setIsOpen({ ...isOpen, [mainLabel]: !isOpen[mainLabel] });
                    shouldOpenSidebar && toggleCollapse();
                  }}
                  {...otherProps}
                >
                  <Item
                    collapsed={collapsed}
                    mainLabel={mainLabel}
                    icon={icon}
                    isOpen={isOpen}
                    haveNestedItems={items ? true : false}
                    showIcon={showIcon}
                    className="cs-ui-sidebar__link-label"
                  />
                </NavLink>
                <motion.div
                  variants={{
                    open: { opacity: 1, height: "max-content" },
                    collapsed: { opacity: 0, scale: 0, height: 0 },
                  }}
                  animate={isOpen[mainLabel] ? "open" : "collapsed"}
                  transition={{ duration: 0.3 }}
                >
                  {items && !collapsed && (
                    <NestedItems
                      items={items}
                      collapsed={collapsed}
                      mainLabel={mainLabel}
                      isOpen={isOpen}
                      setIsOpen={setIsOpen}
                    />
                  )}
                </motion.div>
              </React.Fragment>
            );
          }
        )}
      </div>

      <div className="cs-ui-sidebar__footer">
        {footerLinks?.map(({ label, href, to, icon, ...otherProps }, index) => {
          const IconSVG = icon;
          const isActive = location.pathname.includes(to);
          const Link = href ? "a" : NavLink;
          return (
            <Link
              key={index}
              to={to}
              href={href}
              className="flex items-center no-underline select-none cs-ui-sidebar__link"
              {...otherProps}
            >
              {icon && (
                <NavIconWrapper collapsed={collapsed} mainLabel={label}>
                  <div className="flex items-center justify-center cs-ui-sidebar__link-icon">
                    <IconSVG />
                  </div>
                </NavIconWrapper>
              )}
              {!collapsed && (
                <Typography
                  component="span"
                  style="medium"
                  className="font-medium cs-ui-sidebar__link-label"
                >
                  {label}
                </Typography>
              )}
            </Link>
          );
        })}

        {collapsible && (
          <button
            data-cy="toggle-collapse-button"
            onClick={toggleCollapse}
            className="flex items-center justify-start w-full mb-0 bg-transparent shadow-none select-none cs-ui-sidebar__link cs-ui-sidebar__link--button"
          >
            <span className="flex items-center justify-center cs-ui-sidebar__link-icon">
              {!collapsed ? <LeftArrow size={16} /> : <RightArrow size={16} />}
            </span>
            {!collapsed && (
              <Typography
                component="span"
                style="medium"
                className="font-medium text-left cs-ui-sidebar__link-label"
              >
                {!collapsed ? "Collapse" : ""}
              </Typography>
            )}
          </button>
        )}
      </div>
    </motion.div>
  );
};

Sidebar.propTypes = {
  navLinks: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      to: PropTypes.string,
      icon: PropTypes.oneOfType([PropTypes.element, PropTypes.func]),
      items: PropTypes.arrayOf(
        PropTypes.shape({
          label: PropTypes.string,
          to: PropTypes.string,
        })
      ),
    })
  ),
  footerLinks: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      to: PropTypes.string,
      href: PropTypes.string,
      icon: PropTypes.oneOfType([PropTypes.element, PropTypes.func]),
    })
  ),
  isCollapsed: PropTypes.bool,
  collapsible: PropTypes.bool,
  onCollapse: PropTypes.func,
  appName: PropTypes.string,
};

export default Sidebar;
